import React, { useEffect, useRef, useMemo } from 'react';
import { createOptionsChart, ColorType, AreaSeries, PriceScaleMode, LineType, HistogramSeries } from 'lightweight-charts';

interface TickInfo {
    tick: number;
    price: number;
    liquidityGross: bigint;
}

interface LiquidityConcentrationChartProps {
    symbol: string;
    ticks: TickInfo[];
    price: number;
    decimals: number;
    height?: number;
    loading?: boolean;
}

export const LiquidityConcentrationChart: React.FC<LiquidityConcentrationChartProps> = ({ symbol, ticks, price, decimals, height = 300, loading = false }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<any>(null); // Use any for OptionsChart if types are missing
    const seriesRef = useRef<any>(null);
    const isFirstDataRef = useRef<boolean>(true);

    const chartData = useMemo(() => {
        if (!ticks || ticks.length === 0) return [];

        // Find max/min liquidity for normalization
        let maxLiquidity = BigInt(0);
        let minLiquidity = BigInt(-1);
        ticks.forEach(t => {
            if (t.liquidityGross > maxLiquidity) maxLiquidity = t.liquidityGross;
            if (minLiquidity === BigInt(-1) || t.liquidityGross < minLiquidity) minLiquidity = t.liquidityGross;
        });

        if (maxLiquidity === BigInt(0)) return [];
        const diff = maxLiquidity - minLiquidity;

        // Prepare data points
        // We sort by price for the X-axis
        const sortedTicks = [...ticks].sort((a, b) => a.price - b.price);

        // filter same tick
        const filtedTicks: TickInfo[] = []
        var tick: number | undefined = undefined
        sortedTicks.forEach(t => {
            if (tick !== undefined && tick === t.tick) {
                return
            }
            tick = t.tick
            filtedTicks.push(t)
        })

        const EPSILON = 1e-6;

        // Find the index of the tick closest to the current price
        let closestIndex = -1;
        let minDiff = Infinity;
        filtedTicks.forEach((t, i) => {
            const diff = Math.abs(t.price - price);
            if (diff < minDiff) {
                minDiff = diff;
                closestIndex = i;
            }
        });

        return filtedTicks.map((t, i) => {
            let normalizedValue = 0;
            if (diff > BigInt(0)) {
                normalizedValue = Number((t.liquidityGross - minLiquidity) * BigInt(10000) / diff) / 10000;
            } else {
                normalizedValue = 1.0; // If all values are the same
            }

            return {
                time: t.price as any,
                value: Math.max(EPSILON, normalizedValue),
                color: i === closestIndex ? '#f59e0b' : '#818cf8',
            };
        });
    }, [ticks, price]);

    const formatPrice = (val: number) => {
        val = val * (10 ** decimals);
        if (val < 0.0001) return val.toExponential(4);
        if (val < 1) return val.toFixed(6);
        return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const chart = createOptionsChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: '#94a3b8',
            },
            grid: {
                vertLines: { color: 'rgba(255, 255, 255, 0.05)' },
                horzLines: { color: 'rgba(255, 255, 255, 0.05)' },
            },
            width: chartContainerRef.current.clientWidth,
            height: height,
            timeScale: {
                visible: false, // Hide X-axis
                borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            leftPriceScale: {
                visible: false,
            },
            rightPriceScale: {
                visible: true,
                borderColor: 'rgba(255, 255, 255, 0.1)',
                mode: PriceScaleMode.Logarithmic,
                autoScale: true,
                scaleMargins: {
                    top: 0.05,
                    bottom: 0.05,
                },
            },
            crosshair: {
                horzLine: {
                    visible: true,
                    labelVisible: true,
                },
                vertLine: {
                    visible: true,
                    labelVisible: false,
                },
            },
        });

        const series = chart.addSeries(HistogramSeries, {
            color: '#818cf8',
            priceFormat: {
                type: 'custom',
                formatter: (val: number) => `${val.toFixed(2)}`,
            },
        });

        chartRef.current = chart;
        seriesRef.current = series;

        // Custom Tooltip Logic
        chart.subscribeCrosshairMove((param: any) => {
            if (
                param.point === undefined ||
                !param.time ||
                param.point.x < 0 ||
                param.point.x > chartContainerRef.current!.clientWidth ||
                param.point.y < 0 ||
                param.point.y > height
            ) {
                if (tooltipRef.current) tooltipRef.current.style.display = 'none';
            } else {
                const data = param.seriesData.get(series);
                const price_val = param.time;

                if (!data) {
                    if (tooltipRef.current) tooltipRef.current.style.display = 'none';
                    return;
                }

                if (!tooltipRef.current) return;

                tooltipRef.current.style.display = 'block';
                const liquidity_val = data ? (data.value !== undefined ? data.value : data.close) : null;

                tooltipRef.current.innerHTML = `
                    <div style="color: #a5b4fc; font-size: 11px; margin-bottom: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">${symbol}</div>
                    ${data && data.color === '#f59e0b' ? `<div style="color: #f59e0b; font-size: 13px; font-weight: bold; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
                        <span style="font-size: 14px;">🎯</span> Current Price
                    </div>` : ''}
                    <div style="color: #f1f5f9; font-size: 13px; margin-bottom: 4px;">Price: <span style="font-family: 'JetBrains Mono', monospace; font-weight: 600;">${formatPrice(price_val)}</span></div>
                    ${liquidity_val !== null ? `<div style="color: #818cf8; font-size: 13px; font-weight: bold;">Conc: ${(liquidity_val).toFixed(4)}</div>` : ''}
                `;

                const toolTipWidth = 160;
                const toolTipHeight = 80;
                const toolTipMargin = 15;

                let left = param.point.x + toolTipMargin;
                if (left > chartContainerRef.current!.clientWidth - toolTipWidth) {
                    left = param.point.x - toolTipMargin - toolTipWidth;
                }

                let top = param.point.y + toolTipMargin;
                if (top > height - toolTipHeight) {
                    top = param.point.y - toolTipMargin - toolTipHeight;
                }

                tooltipRef.current.style.left = left + 'px';
                tooltipRef.current.style.top = top + 'px';
            }
        });

        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({ width: chartContainerRef.current.clientWidth });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
        };
    }, [height, symbol]);

    useEffect(() => {
        if (seriesRef.current && chartData.length > 0) {
            seriesRef.current.setData(chartData);

            if (isFirstDataRef.current) {
                chartRef.current.timeScale().fitContent();
                isFirstDataRef.current = false;
            }

            // Add marker for current price
            const currentPriceData = chartData.find(d => d.color === '#f59e0b');
            if (currentPriceData) {
                const markers = [
                    {
                        time: currentPriceData.time,
                        position: 'aboveBar' as any,
                        color: '#f59e0b',
                        shape: 'arrowDown' as any,
                        text: 'Current Price',
                    }
                ];

                if (seriesRef.current.setMarkers) {
                    seriesRef.current.setMarkers(markers);
                }
            }
        }
    }, [chartData]);

    return (
        <div style={{ width: '100%', height, position: 'relative' }}>
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(30, 27, 75, 0.7)',
                    zIndex: 10,
                    borderRadius: '16px',
                    color: '#818cf8',
                    fontWeight: 500,
                    backdropFilter: 'blur(4px)',
                }}>
                    Loading Liquidity Data...
                </div>
            )}
            <div
                ref={chartContainerRef}
                style={{
                    width: '100%',
                    height,
                    background: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: '16px',
                    padding: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.03)'
                }}
            />
            {/* Custom Tooltip Element */}
            <div
                ref={tooltipRef}
                style={{
                    width: '160px',
                    height: 'auto',
                    position: 'absolute',
                    display: 'none',
                    padding: '10px',
                    boxSizing: 'border-box',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    textAlign: 'left',
                    zIndex: 1000,
                    top: '12px',
                    left: '12px',
                    pointerEvents: 'none',
                    backgroundColor: 'rgba(15, 23, 42, 0.9)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                    color: '#fff',
                }}
            />
            {/* Legend/Price Info */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                pointerEvents: 'none',
                textAlign: 'right'
            }}>
                <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Price</div>
                <div style={{ color: '#f59e0b', fontSize: '1rem', fontWeight: 'bold' }}>{formatPrice(price)} {symbol.split('/')[1] || ''}</div>
            </div>
        </div>
    );
};

